import * as t from 'io-ts';
import {isBoolean, isUndefined, mapValues} from 'lodash';

import {UserThemesEnum} from '../../../libs/shared/ui/src/theme/userThemesEnum';
import {TimeFormatsEnum} from '../../helpers/dateTimeHelpers';
import {REmailFormats} from '../../helpers/models/emailFormatHelpers';
import {isTeammateNamespace, isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {ReplyTypesEnum} from '../../helpers/models/recipientHelpers';
import {RAnswer} from './answerModel';
import {
  makeApiSerializableType,
  makeDefaultValueType,
  makeEnumRuntimeType,
  RLazyTeammateIdRef,
  RTagIdRef,
} from './apiTypes';
import {CalendarWeekDaysEnum, RCalendarWeekDay} from './calendarCommonTypes';
import {ConferenceTypesEnum} from './conferenceModel';
import {
  parseCustomSnooze,
  RCustomSnooze,
  RelativeTimeUnitsEnum,
  serializeCustomSnooze,
} from './customSnoozeModel';
import {parseFutureTimeValue, RFutureTimeValue, serializeFutureTimeValue} from './futureTimeValueModel';

/*
 * Runtime type.
 */

export enum DefaultSendActionsEnum {
  SEND = 'send',
  SEND_AND_ARCHIVE = 'sendAndArchive',
  SEND_AND_SNOOZE = 'sendAndSnooze',
}

export enum DefaultReplyActionsEnum {
  REPLY = 'reply',
  REPLY_ALL = 'replyAll',
}

export enum CountersVisibilityModesEnum {
  OPEN = 'open',
  UNREAD = 'unread',
  UNASSIGNED = 'unassigned',
}

export enum SkinTonesEnum {
  NONE = 1,
  LIGHT = 2,
  MEDIUM_LIGHT = 3,
  MEDIUM = 4,
  MEDIUM_DARK = 5,
  DARK = 6,
}

export enum MarkAsReadDelaysEnum {
  NEVER = 'never',
  NO_DELAY_ON_SELECTION = 'onSelection',
  ON_MESSAGE_OPEN = 'noDelay',
  ONE_SECOND = '1sDelay',
  TWO_SECONDS = '2sDelay',
  FIVE_SECONDS = '5sDelay',
}

export enum ReselectDirectionsEnum {
  SMART = 'smart',
  UP = 'up',
  DOWN = 'down',
}

// Type for tips that can be displayed in either an InfoBubble or Infotip.
export enum OnboardingTipTypesEnum {
  TEAMMATE_WELCOME = 'TEAMMATE_WELCOME',
  TEAM_WORKSPACE = 'TEAM_WORKSPACE',
  TEAMMATE_WORKSPACE = 'TEAMMATE_WORKSPACE',
  P2 = 'P2',
  P3 = 'P3',
  COMMENT_COMPOSER = 'COMMENT_COMPOSER',
  GUIDED_DEMO_WELCOME = 'GUIDED_DEMO_WELCOME',
  GUIDED_DEMO_START = 'GUIDED_DEMO_START',
  GUIDED_DEMO_CONVERSATION_VIEWER = 'GUIDED_DEMO_CONVERSATION_VIEWER',
  GUIDED_DEMO_COMMENT_COMPOSER = 'GUIDED_DEMO_COMMENT_COMPOSER',
  GUIDED_DEMO_EDIT_DRAFT = 'GUIDED_DEMO_EDIT_DRAFT',
  GUIDED_DEMO_COMPOSER_SEND = 'GUIDED_DEMO_COMPOSER_SEND',
  GUIDED_DEMO_ARCHIVE = 'GUIDED_DEMO_ARCHIVE',
  GUIDED_DEMO_END = 'GUIDED_DEMO_END',
  READ_STATUS_HIGHLIGHT = 'READ_STATUS_HIGHLIGHT',
  READ_STATUS_CELL = 'READ_STATUS_CELL',
  START_GUIDE_COMMENT_COMPOSER = 'START_GUIDE_COMMENT_COMPOSER',
  PRIVATE_INBOX_COMMENT = 'PRIVATE_INBOX_COMMENT',
  SHARED_INBOX_COMMENT = 'SHARED_INBOX_COMMENT',
  FIRST_CONVERSATION_ARCHIVE = 'FIRST_CONVERSATION_ARCHIVE',
  SEND_AND_ARCHIVE = 'SEND_AND_ARCHIVE',
  COMMENT = 'COMMENT',
  TIP_ARCHIVE = 'ARCHIVE',
  COMMENT_MENTION = 'COMMENT_MENTION',
  DEMO_INBOX_TEAM_WORKSPACE = 'DEMO_INBOX_TEAM_WORKSPACE',
  DEMO_INBOX_RULE = 'DEMO_INBOX_RULE',
  DEMO_INBOX_COMMENT = 'DEMO_INBOX_COMMENT',
  DEMO_INBOX_COMPOSER = 'DEMO_INBOX_COMPOSER',
  HELP_CENTER_TASKS = 'HELP_CENTER_TASKS',
  CANNED_RESPONSES = 'FEATURE_GUIDE_CANNED_RESPONSES',
  RULES = 'FEATURE_GUIDE_RULES',
  SLA_RULES_TRIAL = 'FEATURE_GUIDE_SLA_RULES_TRIAL', // Remove one the trial is over (Oct 15 2020).
  SLA_RULES_UPGRADE = 'FEATURE_GUIDE_SLA_RULES_UPGRADE',
  SLA_RULES = 'FEATURE_GUIDE_SLA_RULES',
  TAGS = 'FEATURE_GUIDE_TAGS',
  ANALYTICS = 'FEATURE_GUIDE_ANALYTICS',
  INTEGRATIONS = 'FEATURE_GUIDE_INTEGRATIONS',
  DEMO_SCHEDULING_MODAL = 'DEMO_SCHEDULING_MODAL',
  WEBINAR_CARD = 'WEBINAR_CARD',
}

export enum ReminderPresetsEnum {
  LATER_TODAY = 'laterToday',
  THIS_EVENING = 'thisEvening',
  TOMORROW = 'tomorrow',
  THIS_WEEK = 'thisWeek',
  NEXT_WEEK = 'nextWeek',
}

export enum KeyboardShortcutsTypesEnum {
  LEGACY = 'legacy',
  NONE = 'none',
  FRONT = 'front',
  GMAIL = 'gmail',
  FRONT_NO_SINGLE_KEY = 'front_no_single_key',
}

export enum StartGuideTaskTypesEnum {
  MENTION_TEAMMATE = 'mention_teammate',
  SEND_EMAIL = 'send_email',
  DOWNLOAD_APP = 'download_app',
  ADD_TEAM_TAGS = 'add_team_tags',
  ADD_TEAM_RULES = 'add_team_rules',
  CELLS_EMPTY_SETUP_ACTION_TEAMMATE_DISMISS = 'cells_empty_setup_action_teammate_dismiss',
  CELLS_EMPTY_SETUP_ACTION_TEAM_DISMISS = 'cells_empty_setup_action_team_dismiss',
  CELLS_EMPTY_SETUP_ACTION_TEAMMATES_DISMISS = 'cells_empty_setup_action_teammates_dismiss',
  HELP_CENTER_SUCCESS_DISMISS = 'help_center_success_dismiss',
  HELP_CENTER_REMINDER_DISMISS = 'help_center_reminder_dismiss',
  ATTEND_LIVE_TRAINING = 'attend_live_training',
  ATTEND_1ON1_TRAINING = 'attend_1on1_training',
}

export enum DismissedAlertsEnum {
  ARCHIVE_TEAM = 'archive_team',
  TRIAGE_TEAM = 'triage_team',
  AUTO_SHARE_DRAFTS = 'auto_share_drafts',
}

export enum TeamInboxesTabsEnum {
  TEAM = 'team',
  TEAMMATE = 'teammate',
}

export enum SwipeActionsEnum {
  NONE = 'none',
  ARCHIVE = 'archive',
  DELETE = 'delete',
  SNOOZE = 'snooze',
  READ = 'read',
  MUTE = 'mute',
  STAR = 'star',
}

export const RReminderPresetsType = makeEnumRuntimeType(ReminderPresetsEnum);
export const ROnboardingTipType = makeEnumRuntimeType(OnboardingTipTypesEnum);
const RCountersVisibilityModes = makeEnumRuntimeType(CountersVisibilityModesEnum);
const RStartGuideTasksType = makeEnumRuntimeType(StartGuideTaskTypesEnum);
const RDismissedAlertsType = makeEnumRuntimeType(DismissedAlertsEnum);

const RDoNotDisturb = t.readonly(
  t.type({
    isEnabled: t.boolean,
    startTime: t.string,
    endTime: t.string,
    timezone: t.string,
  }),
);

export const RRelativeTime = t.readonly(
  t.type({
    relativeTo: makeEnumRuntimeType(RelativeTimeUnitsEnum),
    offset: t.type({
      minute: t.Integer,
      hour: t.Integer,
      day: t.Integer,
      week: t.Integer,
    }),
  }),
);

const RDefaultVideoConference = t.record(
  t.string,
  t.union([makeEnumRuntimeType(ConferenceTypesEnum), t.undefined]),
);

const RReminderPresets = t.readonly(
  t.type({
    [ReminderPresetsEnum.LATER_TODAY]: t.union([RRelativeTime, t.null]),
    [ReminderPresetsEnum.THIS_EVENING]: t.union([RRelativeTime, t.null]),
    [ReminderPresetsEnum.TOMORROW]: t.union([RRelativeTime, t.null]),
    [ReminderPresetsEnum.THIS_WEEK]: t.union([RRelativeTime, t.null]),
    [ReminderPresetsEnum.NEXT_WEEK]: t.union([RRelativeTime, t.null]),
  }),
);

const relativeToShortUnitMap: {[key: string]: RelativeTimeUnitsEnum} = {
  m: RelativeTimeUnitsEnum.MINUTE,
  h: RelativeTimeUnitsEnum.HOUR,
  d: RelativeTimeUnitsEnum.DAY,
  w: RelativeTimeUnitsEnum.WEEK,
};

export const defaultReminderPresets: ReminderPresets = {
  [ReminderPresetsEnum.LATER_TODAY]: {
    relativeTo: RelativeTimeUnitsEnum.MINUTE,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 3,
      [RelativeTimeUnitsEnum.DAY]: 0,
      [RelativeTimeUnitsEnum.WEEK]: 0,
    },
  },
  [ReminderPresetsEnum.THIS_EVENING]: {
    relativeTo: RelativeTimeUnitsEnum.DAY,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 18,
      [RelativeTimeUnitsEnum.DAY]: 0,
      [RelativeTimeUnitsEnum.WEEK]: 0,
    },
  },
  [ReminderPresetsEnum.TOMORROW]: {
    relativeTo: RelativeTimeUnitsEnum.DAY,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 9,
      [RelativeTimeUnitsEnum.DAY]: 1,
      [RelativeTimeUnitsEnum.WEEK]: 0,
    },
  },
  [ReminderPresetsEnum.THIS_WEEK]: {
    relativeTo: RelativeTimeUnitsEnum.DAY,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 9,
      [RelativeTimeUnitsEnum.DAY]: 2,
      [RelativeTimeUnitsEnum.WEEK]: 0,
    },
  },
  [ReminderPresetsEnum.NEXT_WEEK]: {
    relativeTo: RelativeTimeUnitsEnum.WEEK,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 9,
      [RelativeTimeUnitsEnum.DAY]: 0,
      [RelativeTimeUnitsEnum.WEEK]: 1,
    },
  },
};

const RQuickResponsesIds = t.readonlyArray(t.union([t.Integer, t.undefined, t.null]));

const RQuickResponses = t.readonlyArray(t.union([RAnswer, t.undefined]));

const RCustomSnoozes = t.union([t.readonlyArray(RCustomSnooze), t.null]);

const RPreferencesDirty = t.readonly(
  t.type({
    badgeNamespace: t.union([t.undefined, t.string]),
    theme: makeEnumRuntimeType(UserThemesEnum),
    defaultFontStyle: t.string,
    defaultSendAction: makeEnumRuntimeType(DefaultSendActionsEnum),
    defaultReplyType: makeEnumRuntimeType(ReplyTypesEnum),
    shouldReplyInNewConversation: t.boolean,
    lastEmailFormat: REmailFormats,
    emojiSkinTone: makeEnumRuntimeType(SkinTonesEnum),
    markAsReadDelay: makeEnumRuntimeType(MarkAsReadDelaysEnum),
    reselectDirection: makeEnumRuntimeType(ReselectDirectionsEnum),
    shouldPrintWithAttachments: t.boolean,
    shouldSendOnEnterForChat: t.boolean,
    shouldMuteNotifications: t.boolean,
    shouldMuteOnMoveToTeamInbox: t.boolean,
    shouldMuteOnUnassign: t.boolean,
    shouldSubscribeOnComment: t.boolean,
    shouldSubscribeOnReply: t.boolean,
    shouldHideAddIndividualInbox: t.boolean,
    defaultChannelId: t.union([t.Integer, t.undefined]),
    shouldReplyWithDefaultChannel: t.boolean,
    shouldReplyInNewWindow: t.boolean,
    doNotDisturb: RDoNotDisturb,
    hiddenDelegationOwnerIds: t.readonlyArray(t.Integer),
    displayedTeammateIds: t.union([t.readonlyArray(RLazyTeammateIdRef), t.undefined]),
    hiddenFrameIds: t.readonlyArray(t.string),
    sortedFrameIds: t.readonlyArray(t.string),
    visibleCustomViewIds: t.readonlyArray(RTagIdRef),
    reminderValues: t.readonlyArray(RFutureTimeValue),
    reminderPresets: makeDefaultValueType(RReminderPresets, defaultReminderPresets),
    customSnoozes: makeDefaultValueType(RCustomSnoozes, null),
    sendLaterValues: t.readonlyArray(RFutureTimeValue),
    shouldSendUsageData: t.boolean,
    shouldEnablePublicApiAccess: t.boolean,
    countersVisibilities: t.dictionary(t.string, t.union([RCountersVisibilityModes, t.undefined])),
    isReadOnlyAccount: t.boolean,
    quickReponsesIds: RQuickResponsesIds,
    hasSeenWelcomeCarousel: t.boolean,
    pasteAsText: t.boolean,
    defaultNumberPrefix: t.string,
    shouldDisplayPersonaBanner: t.boolean,
    keepLegacyKeyboardShortcuts: t.boolean,
    keyboardShortcutsType: makeEnumRuntimeType(KeyboardShortcutsTypesEnum),
    shouldWarnManyRecipients: t.boolean,
    shouldWarnMissingAttachments: t.boolean,
    wasOnboardingCompleted: t.boolean,
    shouldAlphabetizeWorkspace: makeDefaultValueType(t.boolean, false),
    timeFormat: makeEnumRuntimeType(TimeFormatsEnum),
    seenOnboardingTips: t.readonlyArray(ROnboardingTipType),
    guidedDemoConversationId: t.union([t.Integer, t.undefined]),
    shouldShowNewTagInWorkspaces: makeDefaultValueType(t.boolean, false),
    shouldShowNewTagInCells: makeDefaultValueType(t.boolean, false),
    shouldHideExternalImages: makeDefaultValueType(t.boolean, false),
    shouldUseOpenFilterForTeams: makeDefaultValueType(t.boolean, false),
    hasSeenTeamTabsAlert: makeDefaultValueType(t.boolean, false),
    ignoreQuickRuleTagIds: t.readonlyArray(t.Integer),
    shouldCalendarDisplayWeekends: makeDefaultValueType(t.boolean, true),
    calendarWeekStartDay: makeDefaultValueType(RCalendarWeekDay, CalendarWeekDaysEnum.SUNDAY),
    completedStartGuideTasks: t.readonlyArray(RStartGuideTasksType),
    defaultCalendarVersionId: t.union([t.Integer, t.undefined]),
    lastAutoRecommendation: t.union([t.Integer, t.undefined]),
    lastEditModeHelpTip: t.union([t.Integer, t.undefined]),
    updatedAt: t.Integer,
    hasSeenDomainLoginBanner: makeDefaultValueType(t.boolean, true),
    dismissedAlerts: t.readonlyArray(RDismissedAlertsType),
    recentAnswerIds: t.readonlyArray(t.Integer),
    calendarTimezone: t.union([t.string, t.undefined]),
    visibleInboxIds: t.readonlyArray(t.Integer),
    analyticsPinnedViews: t.readonlyArray(t.Integer),
    analyticsDefaultViews: t.record(t.string, t.union([t.Integer, t.undefined])),
    isBillingContact: t.boolean,
    listAutoFormatting: t.boolean,
    hasDismissedAccountsSetupGuide: makeDefaultValueType(t.boolean, false),
    defaultVideoConferencing: RDefaultVideoConference,
    isVisitorSubscribedAll: t.boolean,
    leftToRightSwipeAction: makeEnumRuntimeType(SwipeActionsEnum),
    rightToLeftSwipeAction: makeEnumRuntimeType(SwipeActionsEnum),
    quickReplyEnabled: t.boolean,
    backButtonOpensP1: t.boolean,
    grammarlyEnabled: t.boolean,
    instantReschedulingLinkEnabled: t.boolean,
  }),
  'preferences',
);

const RPreferencesErrorDirty = t.readonly(
  t.type({
    message: t.union([t.undefined, t.string]),
  }),
);

/*
 * Type.
 */

export interface QuickResponsesIds extends t.TypeOf<typeof RQuickResponsesIds> {}
export interface QuickResponses extends t.TypeOf<typeof RQuickResponses> {}
export interface DoNotDisturb extends t.TypeOf<typeof RDoNotDisturb> {}
export interface RelativeTime extends t.TypeOf<typeof RRelativeTime> {}
export interface ReminderPresets extends t.TypeOf<typeof RReminderPresets> {}
export interface DefaultVideoConferences extends t.TypeOf<typeof RDefaultVideoConference> {}
export interface PreferencesModel extends t.TypeOf<typeof RPreferencesDirty> {}
export interface PreferencesErrorModel extends t.TypeOf<typeof RPreferencesErrorDirty> {}
export const RPreferences = t.clean<PreferencesModel>(RPreferencesDirty as any);

/*
 * IO methods.
 */

export function parsePreferences(src: any): PreferencesModel {
  const {preferences} = src;
  if (!preferences) {
    throw new Error('A preferences property could not be found.');
  }

  return {
    badgeNamespace: parseBadgeNamespace(src, preferences),
    theme: preferences.theme,
    defaultFontStyle: preferences.defaultFontStyle,
    defaultSendAction: preferences.defaultSendAction,
    defaultReplyType: parseDefaultReplyType(preferences.defaultReplyAction),
    shouldReplyInNewConversation: Boolean(preferences.replyInNewConversation),
    lastEmailFormat: preferences.lastEmailFormat,
    emojiSkinTone: preferences.emojiSkinVariation || 1,
    markAsReadDelay: preferences.markAsRead,
    reselectDirection: preferences.conversationDisappearsReselect,
    shouldSendOnEnterForChat: preferences.sendOnEnterForChat,
    shouldMuteNotifications: Boolean(preferences.muteNotifications),
    shouldMuteOnMoveToTeamInbox: Boolean(preferences.muteOnMoveToTeamInbox),
    shouldMuteOnUnassign: Boolean(preferences.muteOnUnassign),
    shouldSubscribeOnComment: Boolean(preferences.followOnComment),
    shouldSubscribeOnReply: Boolean(preferences.followOnReply),
    shouldHideAddIndividualInbox: Boolean(preferences.hideAddIndividualInbox),
    defaultChannelId: preferences.defaultInbox || undefined,
    shouldReplyWithDefaultChannel: Boolean(preferences.replyWithDefaultInbox),
    shouldReplyInNewWindow: Boolean(preferences.replyInPopup),
    doNotDisturb: parseDoNotDisturb(preferences.doNotDisturb),
    hiddenDelegationOwnerIds: preferences.hiddenDelegationOwnerIds || [],
    displayedTeammateIds: preferences.displayedTeammateIds || undefined,
    hiddenFrameIds: preferences.hiddenFrameIds || [],
    sortedFrameIds: preferences.sortedFrameIds || [],
    visibleCustomViewIds: preferences.personalWorkspaceTagIds || [],
    reminderValues: preferences.optionalReminders.map(parseFutureTimeValue),
    reminderPresets: parseReminderPresets(preferences),
    customSnoozes: preferences.customSnoozes && preferences.customSnoozes.map(parseCustomSnooze),
    sendLaterValues: preferences.sendLater.map(parseFutureTimeValue),
    shouldSendUsageData: Boolean(preferences.sendUsageData),
    countersVisibilities: preferences.countersVisibility,
    isReadOnlyAccount: Boolean(preferences.readOnlyAccount),
    quickReponsesIds: [
      preferences.quickResponse1,
      preferences.quickResponse2,
      preferences.quickResponse3,
      preferences.quickResponse4,
      preferences.quickResponse5,
    ],
    hasSeenWelcomeCarousel: isBoolean(preferences.welcomeCarousel) ? preferences.welcomeCarousel : true,
    pasteAsText: preferences.forcePasteAsText,
    defaultNumberPrefix: preferences.defaultNumberPrefix || '+1',
    shouldDisplayPersonaBanner: Boolean(preferences.personnaBanner),
    keepLegacyKeyboardShortcuts: preferences.keepLegacyKeyboardShortcuts,
    keyboardShortcutsType: preferences.keepLegacyKeyboardShortcuts
      ? preferences.keyboardShortcutsWithOldSetting
      : preferences.keyboardShortcuts,
    shouldWarnManyRecipients: Boolean(preferences.warnManyRecipients),
    shouldWarnMissingAttachments: Boolean(preferences.warnMissingAttachments),
    wasOnboardingCompleted: Boolean(preferences.onboardingCompleted),
    shouldAlphabetizeWorkspace: Boolean(preferences.alphabetizeWorkspace),
    timeFormat: preferences.timeFormat,
    seenOnboardingTips: preferences.seenInfoBubbles.filter((v: any) => ROnboardingTipType.is(v)),
    guidedDemoConversationId: preferences.demoConversationId || undefined,
    shouldShowNewTagInWorkspaces: preferences.showNewTagInWorkspaces,
    shouldShowNewTagInCells: preferences.showNewTagInCells,
    shouldHideExternalImages: preferences.hideExternalImages,
    shouldUseOpenFilterForTeams: preferences.teamInboxesTabs === TeamInboxesTabsEnum.TEAMMATE,
    hasSeenTeamTabsAlert: preferences.hasSeenTeamTabsAlert,
    ignoreQuickRuleTagIds: preferences.ignoreQuickRuleTagIds,
    shouldCalendarDisplayWeekends: preferences.shouldCalendarDisplayWeekends,
    calendarWeekStartDay: preferences.calendarWeekStartDay,
    defaultCalendarVersionId: preferences.defaultCalendarVersionId || undefined,
    lastAutoRecommendation: preferences.lastAutoRecommendation || undefined,
    lastEditModeHelpTip: preferences.lastEditModeHelpTip || undefined,
    defaultVideoConferencing: parseDefaultVideoConferencing(preferences.defaultVideoConferencing),
    completedStartGuideTasks: preferences.completedStartGuideTasks.filter((v: any) =>
      RStartGuideTasksType.is(v),
    ),
    updatedAt: src.updated_at,
    hasSeenDomainLoginBanner: isBoolean(preferences.hasSeenDomainLoginBanner)
      ? preferences.hasSeenDomainLoginBanner
      : true,
    dismissedAlerts: (preferences.dismissedAlerts || []).filter((v: any) => RDismissedAlertsType.is(v)),
    recentAnswerIds: preferences.recentAnswerIds || [],
    calendarTimezone: preferences.calendarTimezone || undefined,
    visibleInboxIds: preferences.visibleInboxIds || [],
    analyticsPinnedViews: preferences.analyticsPinnedViews || [],
    analyticsDefaultViews: preferences.analyticsDefaultViews || {},
    isBillingContact: preferences.administrativeContactTopics.billing,
    listAutoFormatting: preferences.listAutoFormatting,
    shouldEnablePublicApiAccess: preferences.enablePublicApiAccess,
    shouldPrintWithAttachments: preferences.printWithAttachments,
    hasDismissedAccountsSetupGuide: preferences.hasDismissedAccountsSetupGuide,
    isVisitorSubscribedAll: preferences.enableVisitorNotifications,
    leftToRightSwipeAction: preferences.leftToRightSwipeAction,
    rightToLeftSwipeAction: preferences.rightToLeftSwipeAction,
    quickReplyEnabled: preferences.quickReplyEnabled,
    backButtonOpensP1: preferences.backButtonOpensP1,
    grammarlyEnabled: preferences.grammarlyIndividual,
    instantReschedulingLinkEnabled: preferences.instantReschedulingLinkEnabled,
  };
}

function parseBadgeNamespace(src: any, preferences: any) {
  switch (preferences.badgeNotifications) {
    case 'disableCounters':
      return undefined;

    case 'myInbox':
    case 'notificationCenter':
      return `tea:${src.id}`;

    default:
      return isTeamNamespace(preferences.badgeNotifications) ? preferences.badgeNotifications : undefined;
  }
}

function parseDefaultReplyType(src: any) {
  switch (src) {
    case 'replyAll':
      return ReplyTypesEnum.ALL;
    default:
      return ReplyTypesEnum.SINGLE;
  }
}

function parseDoNotDisturb(src: any) {
  return {
    isEnabled: src.enabled,
    startTime: src.start_time,
    endTime: src.end_time,
    timezone: src.timezone,
  };
}

function parseReminderPresets(src: any): any {
  // Since we're using `makeDefaultValue`, we're allowed to use a return type of `any`.
  // This can be a complex object to parse, so if anything goes wrong, simply return
  // `undefined` and the validation failure plus `makeDefaultValue` will save us.
  // TODO: There's probably a better way to have makeDefaultValue handle these types of situations.
  try {
    return mapValues(
      {
        laterToday: src.reminderLaterToday,
        thisEvening: src.reminderThisEvening,
        tomorrow: src.reminderTomorrow,
        thisWeek: src.reminderThisWeek,
        nextWeek: src.reminderNextWeek,
      },
      (value) => {
        if (value === null) {
          return null;
        }

        return {
          relativeTo: relativeToShortUnitMap[value.relativeTo],
          offset: {
            [RelativeTimeUnitsEnum.MINUTE]: value.m || 0,
            [RelativeTimeUnitsEnum.HOUR]: value.h || 0,
            [RelativeTimeUnitsEnum.DAY]: value.d || 0,
            [RelativeTimeUnitsEnum.WEEK]: value.w || 0,
          },
        };
      },
    );
  } catch (e) {
    return undefined;
  }
}

function parseDefaultVideoConferencing(src: any): DefaultVideoConferences {
  return src.reduce(
    (acc: DefaultVideoConferences, curr: any) => ({
      ...acc,
      [curr.calendarVersionId]: curr.defaultVideoConference,
    }),
    {},
  );
}

export function parsePreferencesError(src: any): PreferencesErrorModel {
  const err = src.body || src;
  // Ignore transition errors.
  if (!err || err.message === 'transition prevented') {
    return {
      message: undefined,
    };
  }

  const message = err.message || err.data?.message || err.statusText || 'An error occurred';

  return {
    message,
  };
}

export function serializeTeammatePreferences(preferences: PreferencesModel): any {
  const keyboardShortcuts =
    preferences.keyboardShortcutsType === KeyboardShortcutsTypesEnum.LEGACY
      ? {keyboardShortcutsWithOldSetting: preferences.keyboardShortcutsType}
      : {
          keyboardShortcuts: preferences.keyboardShortcutsType,
          keyboardShortcutsWithOldSetting: preferences.keyboardShortcutsType,
        };

  return {
    preferences: {
      theme: preferences.theme,
      defaultSendAction: preferences.defaultSendAction,
      defaultReplyAction: serializeDefaultReplyType(preferences.defaultReplyType),
      replyInNewConversation: preferences.shouldReplyInNewConversation,
      hiddenDelegationOwnerIds: preferences.hiddenDelegationOwnerIds,
      alphabetizeWorkspace: preferences.shouldAlphabetizeWorkspace,
      followOnComment: preferences.shouldSubscribeOnComment,
      followOnReply: preferences.shouldSubscribeOnReply,
      muteOnUnassign: preferences.shouldMuteOnUnassign,
      muteOnMoveToTeamInbox: preferences.shouldMuteOnMoveToTeamInbox,
      sendOnEnterForChat: preferences.shouldSendOnEnterForChat,
      replyInPopup: preferences.shouldReplyInNewWindow,
      defaultInbox: isUndefined(preferences.defaultChannelId) ? null : preferences.defaultChannelId,
      replyWithDefaultInbox: preferences.shouldReplyWithDefaultChannel,
      ...keyboardShortcuts,
      forcePasteAsText: preferences.pasteAsText,
      warnMissingAttachments: preferences.shouldWarnMissingAttachments,
      warnManyRecipients: preferences.shouldWarnManyRecipients,
      conversationDisappearsReselect: preferences.reselectDirection,
      printWithAttachments: preferences.shouldPrintWithAttachments,
      hideExternalImages: preferences.shouldHideExternalImages,
      timeFormat: preferences.timeFormat,
      sendLater: preferences.sendLaterValues.map(serializeFutureTimeValue),
      lastEmailFormat: preferences.lastEmailFormat,
      quickResponse1: preferences.quickReponsesIds[0],
      quickResponse2: preferences.quickReponsesIds[1],
      quickResponse3: preferences.quickReponsesIds[2],
      quickResponse4: preferences.quickReponsesIds[3],
      quickResponse5: preferences.quickReponsesIds[4],
      markAsRead: preferences.markAsReadDelay,
      defaultCalendarVersionId: preferences.defaultCalendarVersionId,
      lastAutoRecommendation: preferences.lastAutoRecommendation,
      lastEditModeHelpTip: preferences.lastEditModeHelpTip,
      calendarWeekStartDay: preferences.calendarWeekStartDay,
      sendUsageData: preferences.shouldSendUsageData,
      enablePublicApiAccess: preferences.shouldEnablePublicApiAccess,
      badgeNotifications: serializeBadgeNamespace(preferences.badgeNamespace),
      defaultNumberPrefix: preferences.defaultNumberPrefix,
      countersVisibility: preferences.countersVisibilities,
      teamInboxesTabs: serializeShouldUseOpenFilterForTeams(preferences.shouldUseOpenFilterForTeams),
      defaultVideoConferencing: serializeDefaultVideoConferencing(preferences.defaultVideoConferencing),
      grammarlyIndividual: preferences.grammarlyEnabled,
      instantReschedulingLinkEnabled: preferences.instantReschedulingLinkEnabled,
    },
  };
}

export function serializePreferences(preferences: PreferencesModel): any {
  return {
    preferences: {
      defaultFontStyle: preferences.defaultFontStyle,
      lastEmailFormat: preferences.lastEmailFormat,
      emojiSkinVariation: serializeEmojiSkinTone(preferences.emojiSkinTone),
      sendOnEnterForChat: preferences.shouldSendOnEnterForChat,
      muteNotifications: preferences.shouldMuteNotifications,
      hideAddIndividualInbox: preferences.shouldHideAddIndividualInbox,
      doNotDisturb: serializeDoNotDisturb(preferences.doNotDisturb),
      hiddenDelegationOwnerIds: preferences.hiddenDelegationOwnerIds,
      displayedTeammateIds: preferences.displayedTeammateIds,
      hiddenFrameIds: preferences.hiddenFrameIds,
      sortedFrameIds: preferences.sortedFrameIds,
      personalWorkspaceTagIds: preferences.visibleCustomViewIds,
      countersVisibility: preferences.countersVisibilities,
      welcomeCarousel: preferences.hasSeenWelcomeCarousel,
      personnaBanner: preferences.shouldDisplayPersonaBanner,
      seenInfoBubbles: preferences.seenOnboardingTips,
      showNewTagInWorkspaces: preferences.shouldShowNewTagInWorkspaces,
      alphabetizeWorkspace: preferences.shouldAlphabetizeWorkspace,
      teamInboxesTabs: serializeShouldUseOpenFilterForTeams(preferences.shouldUseOpenFilterForTeams),
      hasSeenTeamTabsAlert: preferences.hasSeenTeamTabsAlert,
      ignoreQuickRuleTagIds: preferences.ignoreQuickRuleTagIds,
      showNewTagInCells: preferences.shouldShowNewTagInCells,
      shouldCalendarDisplayWeekends: preferences.shouldCalendarDisplayWeekends,
      completedStartGuideTasks: preferences.completedStartGuideTasks,
      hasSeenDomainLoginBanner: preferences.hasSeenDomainLoginBanner,
      dismissedAlerts: preferences.dismissedAlerts,
      recentAnswerIds: preferences.recentAnswerIds,
      calendarTimezone: preferences.calendarTimezone,
      visibleInboxIds: preferences.visibleInboxIds,
      analyticsPinnedViews: preferences.analyticsPinnedViews,
      analyticsDefaultViews: preferences.analyticsDefaultViews,
      isBillingContact: preferences.isBillingContact,
      listAutoFormatting: preferences.listAutoFormatting,
      hasDismissedAccountsSetupGuide: preferences.hasDismissedAccountsSetupGuide,
      enableVisitorNotifications: preferences.isVisitorSubscribedAll,
      leftToRightSwipeAction: preferences.leftToRightSwipeAction,
      rightToLeftSwipeAction: preferences.rightToLeftSwipeAction,
      quickReplyEnabled: preferences.quickReplyEnabled,
      backButtonOpensP1: preferences.backButtonOpensP1,
      customSnoozes: preferences.customSnoozes?.map((s) => serializeCustomSnooze(s)),
      lastAutoRecommendation: preferences.lastAutoRecommendation,
      lastEditModeHelpTip: preferences.lastEditModeHelpTip,
    },
  };
}

export function serializeBadgeNamespace(badgeNamespace: string | undefined) {
  if (badgeNamespace && isTeamNamespace(badgeNamespace)) {
    return badgeNamespace;
  }

  if (badgeNamespace && isTeammateNamespace(badgeNamespace)) {
    return 'myInbox';
  }

  return 'disableCounters';
}

function serializeEmojiSkinTone(skinTone: SkinTonesEnum) {
  return skinTone === SkinTonesEnum.NONE ? null : skinTone;
}

function serializeDoNotDisturb(doNotDisturb: DoNotDisturb) {
  return {
    enabled: doNotDisturb.isEnabled,
    start_time: doNotDisturb.startTime,
    end_time: doNotDisturb.endTime,
    timezone: doNotDisturb.timezone,
  };
}

function serializeDefaultReplyType(defaultReplyType: ReplyTypesEnum) {
  switch (defaultReplyType) {
    case ReplyTypesEnum.ALL:
      return DefaultReplyActionsEnum.REPLY_ALL;
    default:
      return DefaultReplyActionsEnum.REPLY;
  }
}

function serializeShouldUseOpenFilterForTeams(shouldUseOpenFilterForTeams: boolean) {
  return shouldUseOpenFilterForTeams ? TeamInboxesTabsEnum.TEAMMATE : TeamInboxesTabsEnum.TEAM;
}

function serializeDefaultVideoConferencing(defaultVideoConferencing: DefaultVideoConferences) {
  return Object.entries(defaultVideoConferencing).map(([calendarVersionId, defaultVideoConference]) => ({
    calendarVersionId: Number(calendarVersionId),
    defaultVideoConference,
  }));
}

/*
 * API type.
 */

export const ApiPreferences = makeApiSerializableType(
  'preferences',
  RPreferences,
  parsePreferences,
  serializePreferences,
);
